import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@monksflow/monks-ui';
import { Scissors, TriangleAlert } from 'lucide-react';
import type { TaxonomyField, TaxonomyFieldGroup, TaxonomySettings } from '@/entities/taxonomy';
import {
  DEFAULT_FIELD_DELIMITER,
  DEFAULT_SUBFIELD_DELIMITER,
  isFieldInName,
} from '@inference.engine/taxonomy/web';
import { channelStatuses } from '../../lib/taxonomyString';
import { AbbreviationSuggestionsModal } from '../AbbreviationSuggestionsModal/AbbreviationSuggestionsModal';

interface SchemaPreviewProps {
  fields: TaxonomyField[];
  /** Schema-level output settings (placeholder + channel limits). */
  settings: TaxonomySettings;
  /** Platforms selected for this version — drives the per-channel counters. */
  platforms: string[];
  /** Ids needed to request LLM abbreviation suggestions. */
  taxonomyId: string;
  versionId: string;
  /** Apply a pack of abbreviations to value codes. Omit when read-only. */
  onApplyAbbreviations?: (changes: { fieldId: string; value: string; to: string }[]) => void;
  /** Apply date-mask changes from the shortener. Omit when read-only. */
  onApplyDateFormats?: (changes: { fieldId: string; to: string }[]) => void;
  /** Unlink bound fields so their codes can be shortened locally. Omit when read-only. */
  onApplyUnlinks?: (
    changes: { fieldId: string; values: { value: string; code: string }[] }[],
  ) => void;
}

/** Group-tinted pill styling (Media = blue, Content = faint amber) — mirrors the
 *  accent colors of the Schema Content group tables. */
const CHIP: Record<TaxonomyFieldGroup, string> = {
  media:
    'border-sky-200 bg-sky-100 text-sky-900 dark:border-sky-900 dark:bg-sky-950 dark:text-sky-200',
  content:
    'border-amber-100 bg-amber-50 text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/50 dark:text-amber-300',
};

function FieldChip({
  field,
  subfield,
  excluded,
}: {
  field: TaxonomyField;
  subfield?: boolean;
  /** A column that exists but contributes nothing to the generated name. */
  excluded?: boolean;
}) {
  const { t } = useTranslation('knowledge');
  return (
    <span
      title={
        excluded
          ? t('taxonomy.schemaPreview.storedReferenceNotInName', { name: field.name })
          : undefined
      }
      className={`inline-flex items-center gap-1.5 rounded-full border font-medium ${
        excluded
          ? 'border-dashed border-border-light bg-transparent text-muted-foreground/70'
          : CHIP[field.group]
      } ${subfield ? 'px-2 py-0.5 text-[11px] opacity-90' : 'px-2.5 py-1 text-xs'}`}
    >
      <span className={excluded ? 'line-through decoration-muted-foreground/40' : undefined}>
        {field.name}
      </span>
      {field.required && (
        <span className="text-destructive" aria-label={t('taxonomy.schemaPreview.required')}>
          *
        </span>
      )}
      {/* Date fields only — `format` is a date mask. Rendering it for any type
          meant a value stranded by a type change (or typed when the input was
          wrongly offered on every type) showed up here as a mystery badge. */}
      {field.type === 'date' && field.format && (
        <span className="rounded-full bg-black/5 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide dark:bg-white/15">
          {field.format}
        </span>
      )}
    </span>
  );
}

/** A muted delimiter token between chips ("_" between fields, "-" within a cluster). */
function Delim({ char }: { char: string }) {
  return (
    <span className="px-1 font-mono text-sm text-muted-foreground/70" aria-hidden>
      {char}
    </span>
  );
}

/**
 * Live naming preview for the builder: the concatenated schema name as chips
 * (media first then content; subfields join with "-", clusters with "_"), plus a
 * per-channel character counter (worst-case output length vs each platform's
 * limit) and, when a limit is exceeded, one-click abbreviation suggestions that
 * shorten the offending codes.
 */
export function SchemaPreview({
  fields,
  settings,
  platforms,
  taxonomyId,
  versionId,
  onApplyAbbreviations,
  onApplyDateFormats,
  onApplyUnlinks,
}: SchemaPreviewProps) {
  const { t } = useTranslation('knowledge');
  const [suggestOpen, setSuggestOpen] = useState(false);

  /**
   * Every column, in Schema Content's order — including the ones that don't
   * reach the name.
   *
   * A linked join key not opted into the name is a stored reference, so it adds
   * nothing to the string. Dropping it from the preview entirely made the two
   * panels disagree: the column existed in Schema Content but its chip did not,
   * which reads as a wrong order (everything after it looks shifted) and makes
   * dragging that column look like it did nothing. It's shown struck through
   * instead, and still contributes no characters and no delimiter.
   */
  const topLevel = [
    ...fields.filter((f) => f.group === 'media' && !f.parentId),
    ...fields.filter((f) => f.group === 'content' && !f.parentId),
  ];
  const clusters = topLevel.map((parent) => ({
    parent,
    subfields: fields.filter((f) => f.parentId === parent.id),
    inName: isFieldInName(parent),
  }));

  const fieldDelim = settings.fieldDelimiter || DEFAULT_FIELD_DELIMITER;
  const subfieldDelim = settings.subfieldDelimiter || DEFAULT_SUBFIELD_DELIMITER;
  const statuses = channelStatuses(fields, settings, platforms);
  const maxOverBy = statuses.reduce((m, s) => Math.max(m, s.overBy), 0);
  const anyOver = maxOverBy > 0;
  // Fit the STRICTEST exceeded limit so shortening satisfies every channel.
  const strictest = statuses
    .filter((s) => s.over)
    .reduce<
      (typeof statuses)[number] | null
    >((min, s) => (!min || s.limit < min.limit ? s : min), null);

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-border-light bg-card p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-foreground">
          {t('taxonomy.schemaPreview.schemaPreview')}
        </h2>
        <span className="text-xs text-muted-foreground">
          {t('taxonomy.schemaPreview.howGeneratedNameLooksYou')}
        </span>
      </div>

      {clusters.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border-light px-4 py-6 text-center text-sm text-muted-foreground">
          {t('taxonomy.schemaPreview.addFieldsBelowPreviewConcatenated')}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-y-2">
            {clusters.map(({ parent, subfields, inName }, i) => (
              <span key={parent.id} className="flex items-center">
                {/* The delimiter belongs to the STRING, so it's drawn only
                    between chips that are actually in it — counted against the
                    included clusters before this one, not the raw index. */}
                {inName && clusters.slice(0, i).some((c) => c.inName) && (
                  <Delim char={fieldDelim} />
                )}
                <FieldChip field={parent} excluded={!inName} />
                {subfields.map((sub) => {
                  const subInName = isFieldInName(sub);
                  return (
                    <span key={sub.id} className="flex items-center">
                      {subInName && <Delim char={subfieldDelim} />}
                      <FieldChip field={sub} subfield excluded={!subInName} />
                    </span>
                  );
                })}
              </span>
            ))}
          </div>

          {/* Character counter per selected platform (worst-case output length). */}
          {statuses.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-border-light pt-3">
              <span className="text-xs text-muted-foreground">
                <Trans
                  i18nKey="taxonomy.schemaPreview.maxLengthChars"
                  ns="knowledge"
                  values={{ length: statuses[0].length }}
                  components={{ num: <span className="font-mono font-semibold text-foreground" /> }}
                />
              </span>
              <div className="flex flex-wrap items-center gap-1.5">
                {statuses.map((s) => (
                  <span
                    key={s.platform}
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                      s.over
                        ? 'bg-destructive/10 text-destructive'
                        : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300'
                    }`}
                    title={
                      s.over
                        ? t('taxonomy.schemaPreview.overLimit', {
                            platform: s.platform,
                            overBy: s.overBy,
                            limit: s.limit,
                          })
                        : t('taxonomy.schemaPreview.withinLimit', {
                            platform: s.platform,
                            limit: s.limit,
                          })
                    }
                  >
                    {s.over && <TriangleAlert className="size-3" aria-hidden />}
                    {s.platform} {s.length}/{s.limit}
                  </span>
                ))}
              </div>
              {anyOver && onApplyAbbreviations && (
                <Button
                  variant="outline"
                  size="mini"
                  className="ml-auto"
                  onClick={() => setSuggestOpen(true)}
                >
                  <Scissors className="size-3.5" />
                  {t('taxonomy.schemaPreview.shortenAi')}
                </Button>
              )}
            </div>
          )}
        </>
      )}

      {/* `statuses[0]` is safe here: the button that opens this only renders
          when a channel is over its limit, so the list is non-empty. */}
      {suggestOpen && onApplyAbbreviations && (
        <AbbreviationSuggestionsModal
          open
          taxonomyId={taxonomyId}
          versionId={versionId}
          platform={strictest?.platform}
          targetLength={strictest?.limit ?? statuses[0].limit}
          currentLength={statuses[0].length}
          channels={statuses.map((s) => ({
            platform: s.platform,
            limit: s.limit,
            length: s.length,
            over: s.over,
          }))}
          onApply={onApplyAbbreviations}
          onApplyDateFormats={onApplyDateFormats}
          onApplyUnlinks={onApplyUnlinks}
          onClose={() => setSuggestOpen(false)}
        />
      )}
    </div>
  );
}

SchemaPreview.displayName = 'SchemaPreview';
